import React, { useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useWebShare } from 'site-modules/shared/hooks/use-web-share/use-web-share';
import { getDealCheckerShareData } from 'site-modules/lease-deals/utils/get-deal-checker-share-data';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { PaymentRatioInsights } from 'site-modules/lease-deals/components/payment-ratio-insights/payment-ratio-insights';
import { PriceComparisonBar } from 'site-modules/lease-deals/components/price-comparison-bar/price-comparison-bar';
import { DEAL_CHECKER_CREATIVE_ID } from 'site-modules/lease-deals/components/deals-checker-widget/constants';
import { KeyInsightsList } from 'site-modules/lease-deals/components/key-insights-list/key-insights-list';
import {
  EDM_LEASE_DEAL_INSIGHTS_CREATIVE_ID,
  EDM_LEASE_DEALS_VEHICLE_CARD_CREATIVE_ID,
} from 'site-modules/lease-deals/constants/lease-deals-constants';
import { Image } from 'site-modules/shared/components/image/image';
import { SOURCES } from 'client/utils/image-helpers';

export const DealResultView = memo(({ data }) => {
  const {
    effectivePayment,
    effectivePaymentRatio,
    rating,
    dueAtSigning,
    bodyType,
    effectivePaymentMedian,
    msrp,
    monthlyPayment,
    term,
    lifetimeCost,
  } = data;

  const shareData = useMemo(() => getDealCheckerShareData(), []);
  const { isSupported, share } = useWebShare(shareData);

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        subaction_name: 'deal_checker_rating',
        creative_id: DEAL_CHECKER_CREATIVE_ID,
        value: `${rating}`,
      },
    });
  }, [rating]);

  const fireShareTracking = (status, creativeId = DEAL_CHECKER_CREATIVE_ID) => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHARE_CONTENT,
        subaction_name: status,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: creativeId,
        value: `${rating}`,
      },
    });
  };

  const handleShare = () => {
    fireShareTracking(TrackingConstant.SHARE_STARTED);
    share({
      onSuccess: () => fireShareTracking(TrackingConstant.SHARE_COMPLETED, EDM_LEASE_DEALS_VEHICLE_CARD_CREATIVE_ID),
      onError: () => fireShareTracking(TrackingConstant.SHARE_FAILED, EDM_LEASE_DEALS_VEHICLE_CARD_CREATIVE_ID),
    });
  };

  return (
    <div>
      <PaymentRatioInsights
        effectivePaymentRatio={effectivePaymentRatio}
        className="bg-cool-gray-80 rounded-20 p-1_5 d-flex flex-column align-items-center"
      />
      <div className="pt-2">
        <h3 className="size-16 text-cool-gray-10">
          What you’re <i>really</i> paying
        </h3>
        <p className="size-24 text-cool-gray-10 text-center fw-bold">{formatPriceStringV2(effectivePayment)}</p>
        <p className="size-16 text-cool-gray-40 text-center">
          with {formatPriceStringV2(dueAtSigning)} due at signing included
        </p>
      </div>
      <PriceComparisonBar
        effectivePayment={Number(effectivePayment)}
        effectivePaymentRatio={effectivePaymentRatio}
        effectivePaymentMedian={effectivePaymentMedian}
      />
      <div data-tracking-parent={EDM_LEASE_DEAL_INSIGHTS_CREATIVE_ID}>
        <KeyInsightsList
          effectivePayment={Number(effectivePayment)}
          effectivePaymentMedian={effectivePaymentMedian}
          term={term}
          bodyType={bodyType?.toLowerCase()}
          lifetimeCost={lifetimeCost}
          dueAtSigning={dueAtSigning}
          monthlyPayment={monthlyPayment}
          msrp={msrp}
          effectivePaymentRatio={effectivePaymentRatio}
          showEffectivePaymentText
        />
      </div>
      {isSupported && (
        <div className="d-flex justify-content-center mt-1">
          <button
            className="border-1 lease-deals-share-button pos-r bg-white rounded-pill p-0_5 px-1 lh-1 z-1 text-nowrap"
            onClick={handleShare}
            aria-label="Share this deal"
          >
            <Image imageId="/icons/youtube-share.svg" source={SOURCES.STATIC} width={20} height={20} ariaHidden lazy />
            <span className="ms-0_25 align-middle">Share</span>
          </button>
        </div>
      )}
    </div>
  );
});

DealResultView.displayName = 'DealResultView';

DealResultView.propTypes = {
  data: PropTypes.shape({
    effectivePayment: PropTypes.string.isRequired,
    effectivePaymentRatio: PropTypes.number.isRequired,
    dueAtSigning: PropTypes.number.isRequired,
    rating: PropTypes.number.isRequired,
    msrp: PropTypes.number.isRequired,
    monthlyPayment: PropTypes.number.isRequired,
    term: PropTypes.number.isRequired,
    lifetimeCost: PropTypes.number.isRequired,
    bodyType: PropTypes.string,
    effectivePaymentMedian: PropTypes.number,
  }).isRequired,
};
